import * as AlertDialog from "radix-ui/alert-dialog";
import * as Dialog from "radix-ui/dialog";
import { useEffect, useRef, useState, type ReactNode, type SyntheticEvent } from "react";

import { Button, Checkbox, CopyButton, PasswordField, TextField } from "./Controls";
import { InlineResult } from "./Feedback";

export interface ReauthenticationCredentials {
  readonly password: string;
  readonly secondFactor?: string;
}

export function ReauthenticationDialog({
  actionName,
  error,
  onOpenChange,
  onVerify,
  open,
  requireSecondFactor = false,
  submitting = false,
}: {
  readonly actionName: string;
  readonly error?: string | undefined;
  readonly onOpenChange: (open: boolean) => void;
  readonly onVerify: (credentials: ReauthenticationCredentials) => Promise<void> | void;
  readonly open: boolean;
  readonly requireSecondFactor?: boolean;
  readonly submitting?: boolean;
}) {
  const [password, setPassword] = useState("");
  const [secondFactor, setSecondFactor] = useState("");
  useEffect(() => {
    if (!open) {
      setPassword("");
      setSecondFactor("");
    }
  }, [open]);
  const submit = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    const credentials: ReauthenticationCredentials =
      secondFactor.length === 0 ? { password } : { password, secondFactor };
    void onVerify(credentials);
  };
  return (
    <Dialog.Root onOpenChange={onOpenChange} open={open}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content aria-describedby="reauth-description" className="dialog-content">
          <Dialog.Title>Kimliğinizi yeniden doğrulayın</Dialog.Title>
          <Dialog.Description id="reauth-description">
            “{actionName}” işlemini sürdürmek için kimliğinizi doğrulayın. Doğrulama sunucuda beş
            dakika geçerlidir.
          </Dialog.Description>
          <form className="form-stack" onSubmit={submit}>
            <PasswordField
              autoComplete="current-password"
              label="Parola"
              onChange={(event) => {
                setPassword(event.currentTarget.value);
              }}
              required
              value={password}
            />
            {requireSecondFactor ? (
              <TextField
                autoComplete="one-time-code"
                inputMode="numeric"
                label="Doğrulama veya kurtarma kodu"
                onChange={(event) => {
                  setSecondFactor(event.currentTarget.value);
                }}
                required
                value={secondFactor}
              />
            ) : null}
            {error === undefined ? null : <InlineResult tone="error">{error}</InlineResult>}
            <div className="form-actions">
              <Button busy={submitting} busyLabel="Doğrulanıyor…" type="submit">
                Kimliğimi doğrula
              </Button>
              <Dialog.Close asChild>
                <Button variant="secondary">İşlemi iptal et</Button>
              </Dialog.Close>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function DestructiveConfirmDialog({
  confirmationLabel,
  consequence,
  error,
  finalAction,
  name,
  onConfirm,
  onOpenChange,
  open,
  submitting = false,
}: {
  readonly confirmationLabel?: string | undefined;
  readonly consequence: string;
  readonly error?: string | undefined;
  readonly finalAction: string;
  readonly name: string;
  readonly onConfirm: () => Promise<void> | void;
  readonly onOpenChange: (open: boolean) => void;
  readonly open: boolean;
  readonly submitting?: boolean;
}) {
  const [confirmation, setConfirmation] = useState("");
  useEffect(() => {
    if (!open) setConfirmation("");
  }, [open]);
  const confirmed = confirmationLabel === undefined || confirmation === confirmationLabel;
  return (
    <AlertDialog.Root onOpenChange={onOpenChange} open={open}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="dialog-overlay" />
        <AlertDialog.Content className="dialog-content">
          <AlertDialog.Title>{name}</AlertDialog.Title>
          <AlertDialog.Description>{consequence} İşlem geri alınamaz.</AlertDialog.Description>
          {confirmationLabel === undefined ? null : (
            <TextField
              autoComplete="off"
              help={`Onaylamak için “${confirmationLabel}” yazın.`}
              label="Onay metni"
              onChange={(event) => {
                setConfirmation(event.currentTarget.value);
              }}
              value={confirmation}
            />
          )}
          {error === undefined ? null : <InlineResult tone="error">{error}</InlineResult>}
          <div className="form-actions">
            <Button
              busy={submitting}
              busyLabel="İşleniyor…"
              disabled={!confirmed}
              onClick={() => {
                void onConfirm();
              }}
              variant="destructive"
            >
              {finalAction}
            </Button>
            <AlertDialog.Cancel asChild>
              <Button variant="secondary">İşlemi iptal et</Button>
            </AlertDialog.Cancel>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}

export function OneTimeSecret({
  acknowledgement,
  body,
  closeLabel,
  heading,
  onClose,
  secret,
  copyLabel,
}: {
  readonly acknowledgement: string;
  readonly body: string;
  readonly closeLabel: string;
  readonly copyLabel: string;
  readonly heading: string;
  readonly onClose: () => void;
  readonly secret: string;
}) {
  const [acknowledged, setAcknowledged] = useState(false);
  const secretRef = useRef(secret);
  useEffect(
    () => () => {
      secretRef.current = "";
    },
    [],
  );
  const close = () => {
    secretRef.current = "";
    onClose();
  };
  return (
    <Dialog.Root open>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content
          className="dialog-content"
          onEscapeKeyDown={(event) => {
            event.preventDefault();
          }}
          onInteractOutside={(event) => {
            event.preventDefault();
          }}
        >
          <Dialog.Title>{heading}</Dialog.Title>
          <Dialog.Description>{body}</Dialog.Description>
          <output className="card technical" data-private="one-time-secret">
            {secretRef.current}
          </output>
          <CopyButton label={copyLabel} value={secretRef.current} />
          <Checkbox
            checked={acknowledged}
            label={acknowledgement}
            onChange={(event) => {
              setAcknowledged(event.currentTarget.checked);
            }}
          />
          <Button disabled={!acknowledged} onClick={close}>
            {closeLabel}
          </Button>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function RecoveryCodePanel({
  codes,
  onClose,
}: {
  readonly codes: readonly string[];
  readonly onClose: () => void;
}) {
  const [acknowledged, setAcknowledged] = useState(false);
  const codesRef = useRef([...codes]);
  useEffect(
    () => () => {
      codesRef.current = [];
    },
    [],
  );
  const close = () => {
    codesRef.current = [];
    onClose();
  };
  return (
    <section className="card stack" data-private="recovery-codes">
      <h2>Kurtarma kodları</h2>
      <p>Bu kodlar yalnızca şimdi gösterilecek. Her kod bir kez kullanılabilir.</p>
      <ul className="technical">
        {codesRef.current.map((code) => (
          <li key={code}>{code}</li>
        ))}
      </ul>
      <CopyButton label="Tüm kodları kopyala" value={codesRef.current.join("\n")} />
      <Checkbox
        checked={acknowledged}
        label="Kurtarma kodlarını güvenli bir yere kaydettim"
        onChange={(event) => {
          setAcknowledged(event.currentTarget.checked);
        }}
      />
      <Button disabled={!acknowledged} onClick={close}>
        Kurtarma kodlarını kapat
      </Button>
    </section>
  );
}

export function SecuritySection({
  children,
  description,
  title,
}: {
  readonly children: ReactNode;
  readonly description?: string | undefined;
  readonly title: string;
}) {
  return (
    <section className="card stack">
      <div>
        <h2>{title}</h2>
        {description === undefined ? null : <p className="muted">{description}</p>}
      </div>
      {children}
    </section>
  );
}
